import * as React from 'react';
import { Observer } from 'mobx-react-lite';
import { Alert, Button, Collapse, Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Utils } from '../../../common/services/Utils';
import BaseHistoryRecordModel from '../../models/BaseHistoryRecordModel';
import { HistoryRecordFactory } from '../../misc';
import HistoryService from '../../services/HistoryService';
import { ProjectImportSummary } from '../../services/types';
import { HistoryRecordExpandedRow } from '..';
import './ProjectImportHistoryDetails.less';

interface Props {
    record: BaseHistoryRecordModel;
    service: HistoryService;
    diffSplitViewEnabled: boolean;
    toggleDiffSplitView: () => void;
}

const PAGE_SIZE = 20;

const ProjectImportHistoryDetails: React.FC<Props> = ({
    record,
    service,
    diffSplitViewEnabled,
    toggleDiffSplitView
}) => {
    const [page, setPage] = React.useState(0);
    const [loadState, setLoadState] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [details, setDetails] = React.useState<BaseHistoryRecordModel[]>([]);
    const [total, setTotal] = React.useState(0);
    const [detailsExpanded, setDetailsExpanded] = React.useState(false);
    const [retry, setRetry] = React.useState(0);

    React.useEffect(() => {
        let active = true;
        if (!detailsExpanded || !record.projectId || !record.importOperationId) return;
        setLoadState('loading');
        const load = async () => {
            try {
                const result = await service.getProjectImportDetails(
                    record.projectId!,
                    record.importOperationId!,
                    page,
                    PAGE_SIZE
                );
                if (!active) return;
                if (result.isErr()) {
                    setLoadState('error');
                    return;
                }
                setDetails(result.value.historyRecords.map(HistoryRecordFactory.createHistoryRecord));
                setTotal(result.value.total);
                setLoadState('success');
            } catch {
                if (active) setLoadState('error');
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [detailsExpanded, record.projectId, record.importOperationId, page, retry, service]);

    const summary = record.importSummary as ProjectImportSummary;
    const areaColumns: ColumnsType<ProjectImportSummary['areas'][number]> = [
        { title: 'Area', dataIndex: 'area' },
        { title: 'Objects', dataIndex: 'totalObjects' },
        { title: 'Imported', dataIndex: 'imported' },
        { title: 'Created', dataIndex: 'created' },
        { title: 'Updated', dataIndex: 'updated' },
        { title: 'Deleted', dataIndex: 'deleted' }
    ];
    const detailColumns: ColumnsType<BaseHistoryRecordModel> = [
        { title: 'Data Object', dataIndex: 'objectLabel' },
        {
            title: 'Name',
            render: (_, item) => <Observer>{() => <span>{item.displayName}</span>}</Observer>
        },
        { title: 'Action', dataIndex: 'action' },
        { title: 'Date', dataIndex: 'createDate', render: value => Utils.formatDateStringShort(value) }
    ];

    return (
        <>
            <Typography.Text strong>Affected configuration areas</Typography.Text>
            <Table
                size="small"
                rowKey={area => area.area}
                columns={areaColumns}
                dataSource={summary.areas}
                pagination={false}
            />
            <Collapse
                bordered={false}
                className="project-import-object-history"
                style={{ marginBottom: detailsExpanded ? 0 : -16 }}
                activeKey={detailsExpanded ? ['details'] : []}
                onChange={keys =>
                    setDetailsExpanded(Array.isArray(keys) ? keys.includes('details') : keys === 'details')
                }
            >
                <Collapse.Panel header="Object-level history" key="details" style={{ borderBottom: 0 }}>
                    {loadState === 'loading' && <Spin tip="Loading object-level history..." />}
                    {loadState === 'error' && (
                        <Alert
                            type="error"
                            showIcon
                            message="Could not load object-level history."
                            description={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>}
                        />
                    )}
                    {loadState === 'success' && (
                        <Table
                            size="small"
                            rowKey={item => item.id}
                            columns={detailColumns}
                            dataSource={details}
                            pagination={{
                                current: page + 1,
                                pageSize: PAGE_SIZE,
                                total,
                                showSizeChanger: false,
                                onChange: next => setPage(next - 1)
                            }}
                            expandable={{
                                expandedRowRender: item => (
                                    <HistoryRecordExpandedRow
                                        fields={item.fields}
                                        diffSplitViewEnabled={diffSplitViewEnabled}
                                        toggleDiffSplitView={toggleDiffSplitView}
                                    />
                                )
                            }}
                        />
                    )}
                </Collapse.Panel>
            </Collapse>
        </>
    );
};

export default ProjectImportHistoryDetails;
